import {
  $lang,
  useEntityBaseMetadata,
  useEntityMetadata,
  useMeasurementUnitsList,
  useMessagesMetadata,
  useRuleEngineCache,
  useTranslation,
} from '@msdk/common'
import {
  BaseValidationDefinition,
  EntityMetadata,
  FieldDefinition,
  FormFieldDefinition,
  FormNode,
  MessageModel,
  NumericFieldDefinition,
  NumericValue,
  RangeValidationDefinition,
  Severity,
  tryConvert,
  tryConvertDecimalInfo,
  tryGetConversionUM,
} from '@msdk/definitions'
import { FormHelperText, FormHelperTextProps } from '@msdk/ui'
import React from 'react'
import { useRecoilValue } from 'recoil'

import { getTranslatedMu, stringFormat } from '../utils'
import { isMessageVisible } from '../utils/validation-visibility'
import { selectMostSevere } from '../utils/validations'

interface Props {
  messages: string[]
  fieldDef: FieldDefinition
  node?: FormNode
  display?: ValidationMessageDisplay
}

export enum ValidationMessageDisplay {
  Inline = 'inline',
  Summary = 'summary',
}

const RANGE_MESSAGE_CODES = [
  'FRM_RANGE_ERROR',
  'FRM_RANGE_ERROR_MIN',
  'FRM_RANGE_ERROR_MAX',
  'FRM_RANGE_HELPER',
  'FRM_RANGE_HELPER_MIN',
  'FRM_RANGE_HELPER_MAX',
]

export function ValidationMessages(props: Props) {
  const t = useTranslation()
  const formDef = props.node?.value.formDefinition as FormFieldDefinition
  const baseMetadata = useEntityBaseMetadata(props.node?.value.entityName as string) as EntityMetadata
  const entityModel = useEntityMetadata(props.node?.value.entityName as string)
  const mus = useMeasurementUnitsList() ?? []
  const lang = useRecoilValue($lang)

  const rootFormName = props.node?.value.rootFormModelName as string
  const { cachedValidation } = useRuleEngineCache(props.node?.value.entityName as string, rootFormName)

  const messages = useMessagesMetadata(baseMetadata?.msProvider) ?? []
  const msgToShow = selectMostSevere(
    messages.filter((message) => isMessageVisible(message, props.node)),
    props.messages,
    1,
  )

  const getMessageParams = (m: MessageModel): string[] => {
    const params = [...(props.node?.value.validationMessageParams ?? [])].reverse().find((item) => item.code === m.code)
    return params?.params ?? props.node?.value.validationParams ?? []
  }

  const formatDefaultDescription = (m: MessageModel, messageParams: string[]): string => {
    const params = [...messageParams]

    // Params from server side validations already contain the field description as first parameter
    // for the message, but client side ones don't and since in some places it is hard to whire
    // the translation hook in some places this is here to prevent parameter duplication
    const fieldDesc = t(props.fieldDef.idml, props.fieldDef.description)
    if (params.length && fieldDesc === params[0]) {
      params.splice(0, 1)
    }

    return stringFormat(t(m.idml, m.description), t(props.fieldDef.idml, props.fieldDef.description), ...params)
  }

  const formatDescription = (m: MessageModel) => {
    const messageParams = getMessageParams(m)
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const possibleMessages = (cachedValidation.cachedRules[formDef?.name] ?? []).filter((v) => v.msgCode === m.code)
    let v: BaseValidationDefinition | undefined
    if (possibleMessages.length > 1 && messageParams.length) {
      v = possibleMessages.find((msg) => msg.msgParameters === messageParams.join(','))
    } else if (possibleMessages.length >= 1) {
      v = possibleMessages[0]
    }

    if (props.node?.value.listName) {
      const listName = props.node?.value.listName as string
      const listRules = cachedValidation.cachedListRules[listName]
      const msgs = listRules.filter((r) => r.field === props.fieldDef.name && r.msgCode === m.code)
      if (msgs.length > 1 && messageParams.length) {
        v = msgs.find((msg) => msg.msgParameters === messageParams.join(','))
      } else if (msgs.length >= 1) {
        v = msgs[0]
      }
    }

    let formatted = formatDefaultDescription(m, messageParams)

    // When the range was set only by a DataRule (no static RangeValidation in cache),
    // v is undefined but node.value.range is still populated — format it directly.
    if (props.node?.value.range && RANGE_MESSAGE_CODES.includes(m.code)) {
      v = { field: props.node.id, validationType: 'Range', msgCode: m.code } as RangeValidationDefinition
    }

    if (v) {
      switch (v.validationType) {
        case 'Range': {
          const r = props.node?.value.range ?? (v as RangeValidationDefinition)
          const desc = t(m.idml, m.description)
          const fieldDesc = t(props.fieldDef.idml, props.fieldDef.description)
          const minDefined = r.min !== null && r.min !== undefined
          const maxDefined = r.max !== null && r.max !== undefined

          const fieldDef = props.node?.value.fieldDefinition as NumericFieldDefinition
          const decimalInfo = tryConvertDecimalInfo(
            fieldDef.decimalInfo,
            fieldDef.um,
            fieldDef.entityName,
            fieldDef.name,
            fieldDef.step,
          )
          let convertedMin = minDefined ? tryConvert(r.min, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined
          let convertedMax = maxDefined ? tryConvert(r.max, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined

          convertedMin =
            convertedMin !== undefined ? parseFloat(convertedMin.toFixed(decimalInfo?.decimalLimit)) : undefined
          convertedMax =
            convertedMax !== undefined ? parseFloat(convertedMax.toFixed(decimalInfo?.decimalLimit)) : undefined

          const convertedMinNumeric = new NumericValue(convertedMin, undefined, undefined, decimalInfo?.decimalLimit)
          const convertedMaxNumeric = new NumericValue(convertedMax, undefined, undefined, decimalInfo?.decimalLimit)

          const muKey = tryGetConversionUM(fieldDef.um, fieldDef.entityName, fieldDef.name)
          const um = getTranslatedMu(muKey, mus)
          if (minDefined && maxDefined) {
            formatted = stringFormat(
              desc,
              fieldDesc,
              convertedMinNumeric.toStringForView(lang),
              convertedMaxNumeric.toStringForView(lang),
              um,
            )
          } else if (minDefined && !maxDefined) {
            formatted = stringFormat(desc, fieldDesc, convertedMinNumeric.toStringForView(lang), um)
          } else if (maxDefined && !minDefined) {
            formatted = stringFormat(desc, fieldDesc, convertedMaxNumeric.toStringForView(lang), um)
          }
          break
        }
        case 'Suggested':
        case 'Invisible':
        case 'Step':
        case 'Readonly':
        case 'RemoveOption':
        case 'KeepOption':
        case 'Expression':
          formatted = stringFormat(
            t(m.idml, m.description),
            t(props.fieldDef.idml, props.fieldDef.description),
            ...(v.msgParameters ?? '').split(',').map((p) => {
              if (p.includes('FE.')) {
                // Since the parameter is a form field, we need to get its definition from the form.
                const param = p.replace('FE.', '').trim()
                const formField = entityModel?.getForm(rootFormName)?.getFieldDefinition(param)
                return t(formField?.idml ?? '', formField?.description ?? '') || p
              } else {
                // In this case it's a standard field, we need to get its definition from the entity.
                const paramField = (entityModel?.fields ?? []).find((f) => f.name === p.trim())
                return t(paramField?.idml ?? '', paramField?.description ?? '') || p
              }
            }),
          )
          break
        case 'Mandatory':
          break
        default:
          throw new Error('Unsupported validation type: ' + v.validationType)
      }
      // if there is no validation the message may have been added from custom logic
    }

    return formatted
  }

  return msgToShow.map((m) => {
    const formattedDescription = formatDescription(m)
    if (!formattedDescription) return <></>

    return (
      <FormHelperText key={m.code} state={getStateFromType(m.type)}>
        {formattedDescription}
      </FormHelperText>
    )
  })
}

const severityTypeMap: Record<Severity, FormHelperTextProps['state']> = {
  [Severity.Mandatory]: 'error',
  [Severity.Error]: 'error',
  [Severity.Warning]: 'warning',
  [Severity.Info]: 'information',
  [Severity.None]: 'enabled',
}

const getStateFromType = (type: Severity): FormHelperTextProps['state'] => {
  return severityTypeMap[type]
}
